import {CommonActions, useNavigation} from '@react-navigation/native';
import React from 'react';
import {useSelector} from 'react-redux';

import {Screens} from '../../constants/navigation';
import {HomeEmulatedDrawerNavigatorScreenProps} from '../../navigators/HomeDrawerNavigator';
import {
  connectedPeakSelector,
  currentDeviceSelector,
  updateDeviceHeatProfile,
} from '../redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../redux/slices/moodLightSlice';
import {heatProfilesMapSelector} from '../redux/slices/profilesSlice';
import {useAppDispatch} from '../redux/useAppDispatch';
import {MaybeAggregatedHeatProfile} from '../types';
import {toHeatCycle} from '../utils/toHeatCycle';
import {useAsyncFn} from './useAsyncFn';

interface Options {
  index: number;
  profile: MaybeAggregatedHeatProfile;
}

type Navigation = HomeEmulatedDrawerNavigatorScreenProps<
  typeof Screens.SaveToDevice
>['navigation'];

export const useSaveHeatProfileToDevice = () => {
  const profiles = useSelector(heatProfilesMapSelector);
  const dispatch = useAppDispatch();
  const navigation = useNavigation<Navigation>();
  const peak = useSelector(connectedPeakSelector);
  const currentDevice = useSelector(currentDeviceSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const [{loading}, replaceWith] = useAsyncFn(
    async ({index, profile}: Options) => {
      if (!peak || !currentDevice) return;

      const mapped = {...profile, order: index};

      await peak.cycles
        .writeHeatCycle(toHeatCycle(mapped, id => moodLights[id]))
        .then(() => {
          dispatch(
            updateDeviceHeatProfile({
              id: currentDevice.id,
              heatProfile: mapped,
            }),
          );
        });

      navigation.dispatch(state => {
        const routes = state.routes.slice(0, -2);

        return CommonActions.reset({
          ...state,
          routes,
          index: routes.length - 1,
        });
      });
    },
    [profiles, moodLights],
  );

  return React.useMemo(
    () => [{isLoading: loading}, replaceWith] as const,
    [loading, replaceWith],
  );
};
